/**
 * ForeignTariffPanel — 상대국 관세(수출). 관세청 국가별 관세율표(공공데이터포털, 서버 foreign_tariff)에서
 *   이 세번(6자리) 아래 나라별 현지 세번·일반 세율·한국 협정세율을 보인다.
 *   FTA 센터 탭은 협정 당사국만(members), 보고서는 summarizeForeignTariff 로 요약만 싣는다.
 */
import { useEffect, useState } from "react";

const ASEAN10 = ["브루나이", "캄보디아", "인도네시아", "라오스", "말레이시아", "미얀마", "필리핀", "싱가포르", "베트남", "태국"];

// 협정 자료와 관세율표 파일의 나라 이름 표기가 다른 곳
const ALIAS = { 아랍에미리트연합국: "아랍에메리트" };

/** 협정 당사국 이름 → 관세율표 나라 이름(EU·아세안 묶음 펼침). */
export function memberCountries(agreement) {
  const out = new Set();
  for (const m of (agreement && agreement.members) || []) {
    if (/아세안/.test(m)) ASEAN10.forEach((x) => out.add(x));
    else if (/^EU|유럽연합/.test(m) || (agreement && agreement.id === "eu")) out.add("EU");
    else {
      const name = String(m).replace(/\s*\(.*\)$/, "");
      out.add(ALIAS[name] || name);
    }
  }
  if (agreement && agreement.id === "eu") out.add("EU");
  return out;
}

const pct = (v) => (v == null ? "—" : `${Math.round(v * 100) / 100}%`);
const range = (a, b) => (a == null ? "—" : a === b ? pct(a) : `${pct(a)}~${pct(b)}`);

/** 보고서용 요약 — 나라별 일반 세율 범위·한국 협정 최저, 한국 협정이 일반보다 낮은 나라 먼저. */
export function summarizeForeignTariff(data, limit = 20) {
  const list = ((data && data.countries) || []).map((c) => ({
    country: c.country,
    version: c.version || "",
    general: range(c.general_min, c.general_max),
    korea: c.korea_min == null ? (c.rows || []).some((r) => (r.korea || []).length) ? "원문 참조" : "—" : pct(c.korea_min),
    saving: c.general_max != null && c.korea_min != null ? c.general_max - c.korea_min : -1,
    lines: c.row_count || 0,
  }));
  list.sort((a, b) => b.saving - a.saving || a.country.localeCompare(b.country));
  return list.slice(0, limit).map(({ saving, ...r }) => r);
}

export default function ForeignTariffPanel({ hsk, agreement }) {
  const hs6 = String(hsk || "").replace(/\D/g, "").slice(0, 6);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (hs6.length !== 6) return undefined;
    let alive = true;
    setData(null);
    fetch(`/api/onto/foreign-tariff?hs6=${hs6}`)
      .then((r) => (r.ok ? r.json() : { countries: [] }))
      .then((d) => alive && setData(d))
      .catch(() => alive && setData({ countries: [] }));
    return () => {
      alive = false;
    };
  }, [hs6]);
  if (hs6.length !== 6) return null;
  if (!data) return <p className="fta-note">상대국 관세율표를 불러오는 중…</p>;
  const members = agreement ? memberCountries(agreement) : null;
  const countries = (data.countries || []).filter((c) => !members || members.has(c.country));
  return (
    <section className="fta-sec" data-testid="foreign-tariff">
      <h3>상대국 관세(수출) — 제{hs6.slice(0, 4)}.{hs6.slice(4, 6)}호</h3>
      {countries.length === 0 ? (
        <p className="fta-note">
          {members ? "이 협정 당사국의 관세율표에 이 세번이 없습니다(자료가 없는 나라일 수 있습니다)." : "이 세번의 상대국 관세율표 자료가 없습니다."}
        </p>
      ) : (
        countries.map((c) => (
          <div key={c.country} style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {c.country} <span className="fta-muted">· 일반 {range(c.general_min, c.general_max)} · 한국 협정 최저 {pct(c.korea_min)} · 현지 세번 {c.row_count}개{c.version ? ` · ${c.version.slice(0, 4)}년 자료` : ""}</span>
            </div>
            <table className="fta-table">
              <thead>
                <tr><th>현지 세번</th><th>품명</th><th>일반 세율</th><th>한국 협정세율</th></tr>
              </thead>
              <tbody>
                {(c.rows || []).map((r) => (
                  <tr key={r.local_code}>
                    <td className="nowrap fta-code">{r.local_code}</td>
                    <td>{r.name_ko || r.name_en}</td>
                    <td className="nowrap fta-rate" title={r.general_label}>{r.general_rate || "—"}</td>
                    <td>{(r.korea || []).length ? r.korea.map((k) => <div key={k.label} title={k.label}><span className="fta-rate">{k.rate}</span> <span className="fta-muted">{k.label.length > 24 ? k.label.slice(0, 24) + "…" : k.label}</span></div>) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {c.row_count > (c.rows || []).length && <div className="fta-muted">현지 세번 {c.row_count}개 중 {(c.rows || []).length}개만 보입니다.</div>}
          </div>
        ))
      )}
      <p className="fta-note">출처: {data.source} — 나라마다 세번 자릿수가 달라 6자리로 맞춰 보였습니다. 협정세율은 원산지 요건을 충족해야 적용되고, 협정세율이 일반(최혜국) 세율보다 높으면 일반 세율이 적용됩니다. 최신 세율은 상대국 세관에서 확인하세요.</p>
    </section>
  );
}
